/**
 * Stripe Pricebook Service
 *
 * When Gobodo is on: Pricebook CRUD uses the generic record API (Pricebook__c) via
 * getRecordsForObject / getRecordById / createRecord / updateRecord / deleteRecord.
 * Firm and partner are set via FirmRelField__c and PartnerRelField__c; the backend
 * creates relationship instances. Pricebook entries (Stripe prices) still use
 * StripeAccountsApi (listStripePricesByPricebook, create/update/delete price).
 * Used only when shouldUseGobodo() is true.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import { getCustomSuffix } from '../config/gobodoConfig';
import {
  getRecordsForObject,
  getRecordById,
  createRecord,
  updateRecord,
  deleteRecord,
  type GenericRecord,
} from './recordStorageService';
import type { Pricebook, PricebookEntry } from '../types/index';

const PRICEBOOK_OBJECT = 'Pricebook';

function firmRelFieldName(): string {
  return `FirmRelField${getCustomSuffix()}`;
}
function partnerRelFieldName(): string {
  return `PartnerRelField${getCustomSuffix()}`;
}

/** Map GenericRecord (Pricebook__c from Gobodo, with accountId/partnerId set by convertFromGobodoRecord) to Pricebook */
function genericRecordToPricebook(record: GenericRecord): Pricebook {
  const name = record.name ?? record.label ?? record.Name__c ?? '';
  const now = new Date().toISOString();
  const isStandardRaw = record.IsStandard__c ?? record.isStandard;
  const isActiveRaw = record.IsActive__c ?? record.isActive;
  const useStandardRaw = record.UseStandardPricebook__c ?? record.useStandardPricebook;
  const descriptionVal = record.Description__c ?? record.description;
  return {
    id: record.id,
    name: String(name),
    description: descriptionVal != null && descriptionVal !== '' ? String(descriptionVal) : undefined,
    isStandard: isStandardRaw === true || isStandardRaw === 'true',
    isActive: isActiveRaw !== false && isActiveRaw !== 'false',
    createdDate: record.createdDate ?? now,
    lastModifiedDate: record.lastModified ?? record.lastModifiedDate ?? now,
    createdBy: record.createdBy ?? 'system',
    accountId: record.accountId ?? undefined,
    useStandardPricebook:
      useStandardRaw === true || useStandardRaw === 'true'
        ? true
        : useStandardRaw === false || useStandardRaw === 'false'
          ? false
          : undefined,
    partnerId: record.partnerId ?? undefined,
  };
}

/** Stripe Price from listStripePricesByPricebook: unitAmount is in cents */
function stripePriceToPricebookEntry(
  raw: any,
  pricebookIdOrKey: string
): PricebookEntry {
  const price = raw?.price ?? raw?.stripePrice ?? raw;
  const id = price?.id ?? raw?.id ?? '';
  const productRaw =
    price?.product ??
    price?.productId ??
    raw?.product ??
    raw?.productId ??
    raw?.product_id ??
    price?.product_id;
  const productId =
    typeof productRaw === 'object' && productRaw != null && typeof productRaw.id === 'string'
      ? productRaw.id
      : String(productRaw ?? '');
  const unitAmount = price?.unitAmount ?? price?.unit_amount ?? raw?.unitAmount ?? raw?.unit_amount ?? 0;
  const currency = (price?.currency ?? raw?.currency ?? 'USD').toString().toUpperCase();
  const active = price?.active ?? raw?.active ?? true;
  const metadata = price?.metadata ?? raw?.metadata;
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const recurringRaw = price?.recurring ?? raw?.recurring;
  const validIntervals = ['day', 'week', 'month', 'year'] as const;
  const recurring =
    recurringRaw != null && typeof recurringRaw === 'object'
      ? (() => {
          const intervalStr = (recurringRaw.interval ?? 'month').toString().toLowerCase();
          const normalizedInterval = validIntervals.includes(intervalStr as any) ? (intervalStr as (typeof validIntervals)[number]) : 'month';
          const intervalCount = recurringRaw.intervalCount ?? recurringRaw.interval_count;
          const count = typeof intervalCount === 'number' && intervalCount >= 1 ? intervalCount : undefined;
          return { interval: normalizedInterval, intervalCount: count };
        })()
      : undefined;
  const billingSchemeRaw = price?.billingScheme ?? raw?.billingScheme ?? price?.billing_scheme ?? raw?.billing_scheme;
  const billingScheme = billingSchemeRaw === 'tiered' ? 'tiered' : billingSchemeRaw === 'per_unit' ? 'per_unit' : undefined;
  const tiersModeRaw = price?.tiersMode ?? raw?.tiersMode ?? price?.tiers_mode ?? raw?.tiers_mode;
  const tiersMode = tiersModeRaw === 'volume' ? 'volume' : tiersModeRaw === 'graduated' ? 'graduated' : undefined;
  const tiersRaw = price?.tiers ?? raw?.tiers;
  const tiers =
    Array.isArray(tiersRaw) && tiersRaw.length > 0
      ? tiersRaw.map((t: any) => {
          const upToRaw = t.up_to ?? t.upTo;
          const upTo =
            upToRaw === 'inf' || upToRaw === undefined
              ? ('inf' as const)
              : typeof upToRaw === 'number'
                ? upToRaw
                : parseInt(String(upToRaw), 10);
          const unitAmountT = t.unit_amount ?? t.unitAmount;
          const flatAmountT = t.flat_amount ?? t.flatAmount;
          return {
            upTo: typeof upTo === 'number' && !isNaN(upTo) ? upTo : ('inf' as const),
            ...(unitAmountT != null && { unitAmount: Number(unitAmountT) }),
            ...(flatAmountT != null && { flatAmount: Number(flatAmountT) }),
          };
        })
      : undefined;
  const firstTierUnitAmount =
    tiers && tiers.length > 0 && tiers[0].unitAmount != null ? tiers[0].unitAmount / 100 : undefined;
  const unitAmountNum = typeof unitAmount === 'number' && !isNaN(unitAmount) ? unitAmount : Number(unitAmount);
  const unitPriceValue = typeof unitAmountNum === 'number' && !isNaN(unitAmountNum) ? unitAmountNum / 100 : 0;
  const unitPrice =
    billingScheme === 'tiered' && firstTierUnitAmount != null && !isNaN(firstTierUnitAmount)
      ? firstTierUnitAmount
      : unitPriceValue;
  return {
    id,
    pricebookId: pricebookIdOrKey,
    productId,
    unitPrice,
    currency,
    isActive: !!active,
    effectiveDate: (meta.effectiveDate as string) || undefined,
    expirationDate: (meta.expirationDate as string) || undefined,
    ...(recurring != null && { recurring }),
    ...(billingScheme && { billingScheme }),
    ...(tiersMode && { tiersMode }),
    ...(tiers && tiers.length > 0 && { tiers }),
  };
}

export async function listPricebooks(): Promise<Pricebook[]> {
  const records = await getRecordsForObject(PRICEBOOK_OBJECT);
  return records.map(genericRecordToPricebook);
}

export async function getPricebook(idOrKey: string): Promise<Pricebook> {
  const record = await getRecordById(PRICEBOOK_OBJECT, idOrKey);
  if (!record) throw new Error(`Pricebook not found: ${idOrKey}`);
  return genericRecordToPricebook(record);
}

export type CreatePricebookPayload = {
  name: string;
  description?: string;
  isStandard?: boolean;
  isActive?: boolean;
  accountId?: string;
  useStandardPricebook?: boolean;
  partnerId?: string;
};

export async function createPricebook(payload: CreatePricebookPayload): Promise<Pricebook> {
  const firmField = firmRelFieldName();
  const partnerField = partnerRelFieldName();
  const recordData: Record<string, any> = {
    name: payload.name,
    description: payload.description ?? '',
  };
  if (payload.accountId !== undefined && payload.accountId !== '') {
    recordData[firmField] = payload.accountId;
  }
  if (payload.partnerId !== undefined && payload.partnerId !== '') {
    recordData[partnerField] = payload.partnerId;
  }
  if (payload.isStandard !== undefined) recordData.isStandard = payload.isStandard;
  if (payload.isActive !== undefined) recordData.isActive = payload.isActive;
  if (payload.useStandardPricebook !== undefined) recordData.useStandardPricebook = payload.useStandardPricebook;

  const created = await createRecord(PRICEBOOK_OBJECT, recordData);
  return genericRecordToPricebook(created);
}

export type UpdatePricebookPayload = Partial<CreatePricebookPayload> & { name?: string };

export async function updatePricebook(id: string, updates: UpdatePricebookPayload): Promise<Pricebook> {
  const firmField = firmRelFieldName();
  const partnerField = partnerRelFieldName();
  const recordUpdates: Record<string, any> = {};
  if (updates.name !== undefined) recordUpdates.name = updates.name;
  if (updates.description !== undefined) recordUpdates.description = updates.description;
  if (updates.accountId !== undefined) recordUpdates[firmField] = updates.accountId;
  if (updates.partnerId !== undefined) recordUpdates[partnerField] = updates.partnerId;
  if (updates.isStandard !== undefined) recordUpdates.isStandard = updates.isStandard;
  if (updates.isActive !== undefined) recordUpdates.isActive = updates.isActive;
  if (updates.useStandardPricebook !== undefined) recordUpdates.useStandardPricebook = updates.useStandardPricebook;

  const updated = await updateRecord(PRICEBOOK_OBJECT, id, recordUpdates, { gobodoPropertyPatch: true });
  if (!updated) throw new Error(`Pricebook not found: ${id}`);
  return genericRecordToPricebook(updated);
}

export async function deletePricebook(id: string): Promise<void> {
  await deleteRecord(PRICEBOOK_OBJECT, id);
}

export async function listPricebookEntries(pricebookIdOrKey: string): Promise<PricebookEntry[]> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client) as any;
  const data = await api.listStripePricesByPricebook(pricebookIdOrKey);
  const list = Array.isArray(data) ? data : [];
  return list.map((item: any) => stripePriceToPricebookEntry(item, pricebookIdOrKey));
}
